import {
  API,
  BASE,
  V1,
  URL_TENANTS,
  URL_MY_TENANTS,
  URL_INVITE,
  URL_INVITATIONS,
  URL_ACCEPT,
  ME,
} from '@/lib/constants/urls';
import { client } from '@/api/common';
import {
  UserTenant,
  Tenant,
  InviteUserData,
  InvitationDetails,
  TenantResponse,
} from './models';
import type {
  CreateTenantDto,
  UpdateTenantDto,
  ListResponse,
  BaseFilterDto,
} from '@repo/api';
import { TenantInvitation } from '@repo/database/browser';
import { AddNullToUndefined } from '@repo/utils';

export const getUserTenants = async (): Promise<UserTenant[]> => {
  const response = await client<UserTenant[]>({
    url: `${BASE}${API}${V1}${URL_TENANTS}${URL_MY_TENANTS}`,
    method: 'GET',
  });

  return response.data;
};

// Create a new tenant (registration flow)
export const createTenant = async (
  data: CreateTenantDto
): Promise<{ tenant: Tenant; owner: unknown }> => {
  const response = await client<{ tenant: Tenant; owner: unknown }>({
    url: `${BASE}${API}${V1}${URL_TENANTS}`,
    method: 'POST',
    data,
  });

  return response.data;
};

// Invite user to tenant
export const inviteUserToTenant = async (
  tenantId: string,
  data: InviteUserData
): Promise<unknown> => {
  const response = await client<unknown>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_INVITE}`,
    method: 'POST',
    data,
  });

  return response.data;
};

export const fetchInvitedUser = async (
  tenantId: string,
  params: BaseFilterDto
): Promise<ListResponse<TenantInvitation>> => {
  const response = await client<ListResponse<TenantInvitation>>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_INVITATIONS}`,
    method: 'GET',
    params,
  });

  return response.data;
};

// Get invitation details by token (public endpoint)
export const getInvitationDetails = async (
  token: string
): Promise<InvitationDetails> => {
  const response = await client<InvitationDetails>({
    url: `${BASE}${API}${V1}${URL_TENANTS}${URL_INVITATIONS}/${token}`,
    method: 'GET',
    skipAuth: true,
  });

  return response.data;
};

// Accept invitation (public endpoint)
export const acceptInvitation = async (data: {
  token: string;
  name?: string;
  password?: string;
}): Promise<unknown> => {
  const response = await client<unknown>({
    url: `${BASE}${API}${V1}${URL_TENANTS}${URL_INVITATIONS}${URL_ACCEPT}`,
    method: 'POST',
    data,
    skipAuth: true,
  });

  return response.data;
};

// Update tenant
export const updateTenant = async (
  id: string,
  data: AddNullToUndefined<UpdateTenantDto>
): Promise<Tenant> => {
  const response = await client<Tenant>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${id}`,
    method: 'PATCH',
    data,
  });

  return response.data;
};

export const getTenantMe = async (
  tenantId: string
): Promise<TenantResponse> => {
  const response = await client<TenantResponse>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${ME}`,
    method: 'GET',
  });

  return response.data;
};

export const deleteTenant = async (id: string, vatId: string) => {
  const response = await client({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${id}`,
    method: 'DELETE',
    data: { vatId },
  });
  return response.data;
};

/** Ripensamento entro i 30 giorni: annulla la cancellazione programmata. */
export const cancelTenantDeletion = async (id: string) => {
  const response = await client({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${id}/cancel-deletion`,
    method: 'POST',
  });
  return response.data;
};

export const revokeInvitation = async (id: string, invitationId: string) => {
  const response = await client({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${id}${URL_INVITATIONS}/${invitationId}`,
    method: 'DELETE',
  });
  return response.data;
};
